import { forwardRef, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';

export interface IFormaPagamentoOpcao {
  id: string;
  nome: string;
  icon: React.ReactNode;
}

interface IPaymentMethodSelectorProps {
  formas: IFormaPagamentoOpcao[];
  // Forma já CONFIRMADA — é o que o painel de detalhes reflete (ver
  // Dialogsendnfce.tsx/Dialogsendnfe.tsx).
  selecionadoId: string;
  // Forma sob o "cursor" de navegação — pode divergir de selecionadoId
  // enquanto o operador está só passeando com a seta, antes de confirmar.
  // Controlado pelo pai pra ficar em sincronia com os atalhos numéricos
  // (que confirmam direto, sem passar por aqui — ver isEventoDeDigitacao).
  destacadoId: string;
  onDestacar: (id: string) => void;
  // Confirma uma forma — quem chama decide o que fazer depois (focar o
  // próximo campo obrigatório da forma recém-confirmada). O segundo
  // parâmetro diz se veio do teclado (Enter) ou de um clique de mouse: por
  // clique o operador espera continuar navegando a listagem com ↑↓ logo em
  // seguida, então quem implementa isso NÃO deve avançar o foco nesse caso
  // (só no Enter) — ver Dialogsendnfce.tsx/Dialogsendnfe.tsx.
  onConfirmar: (id: string, viaTeclado: boolean) => void;
  disabled?: boolean;
  // 'vertical': lista na lateral da dialog (uma opção por linha, ocupa a
  // largura toda da coluna) — usado hoje pelas duas dialogs de emissão
  // fiscal. 'horizontal' (padrão): grade lado a lado, mantido caso essa
  // tela volte a precisar desse layout no futuro.
  orientation?: 'horizontal' | 'vertical';
}

// Setas SÓ navegam (destacam uma opção, sem confirmar nada ainda) — Enter é
// quem confirma o destaque e dispara o efeito de trocar de fato a forma de
// pagamento (abre os campos dela, foca o primeiro obrigatório). Os atalhos
// numéricos (1-9) continuam confirmando direto, sem passar por esse passo
// intermediário — são tratados no componente PAI (nível de dialog, via
// isEventoDeDigitacao), que também sincroniza o destaque pra não ficar
// "preso" numa opção diferente da que os números acabaram de confirmar.
// Encaminha o ref pro <div role="listbox"> — é nele que o foco precisa
// cair quando a dialog abre (ver Dialogsendnfce.tsx/Dialogsendnfe.tsx), já
// que as setas só disparam handleKeyDown quando ESTE contêiner específico
// está focado. Sem isso, nada tinha foco nenhum ao abrir e as setas não
// navegavam em lugar nenhum (só depois do operador clicar na lista).
export const PaymentMethodSelector = forwardRef<HTMLDivElement, IPaymentMethodSelectorProps>(({
  formas, selecionadoId, destacadoId, onDestacar, onConfirmar, disabled, orientation = 'horizontal',
}, ref) => {
  const theme = useTheme();
  const isVertical = orientation === 'vertical';
  // Refs dos itens da lista vertical — pra rolar o item destacado pra dentro
  // da área visível ao navegar com as setas, mesmo padrão da lista de
  // Faturamento (ver listaVertical em SeletorFormasPagamento.tsx). Sem isso,
  // navegar além do fim visível da lista (que agora rola só ela, não a
  // dialog inteira) deixava o item ativo escondido, fora da área com scroll.
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!isVertical) return;
    const indice = formas.findIndex((f) => f.id === destacadoId);
    itemRefs.current[indice]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isVertical, destacadoId, formas]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const indiceAtual = formas.findIndex((f) => f.id === destacadoId);
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      const proximo = formas[Math.min(formas.length - 1, indiceAtual + 1)];
      if (proximo) onDestacar(proximo.id);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      const anterior = formas[Math.max(0, indiceAtual - 1)];
      if (anterior) onDestacar(anterior.id);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onConfirmar(destacadoId, true);
    }
  };

  const corBorda = theme.palette.mode === 'dark' ? theme.palette.divider : '#D9E2EA';

  return (
    <Box sx={isVertical ? { height: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
          Forma de Pagamento
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          ↑↓ navega, <b>ENTER</b> seleciona — ou 1-{Math.min(formas.length, 9)}
        </Typography>
      </Box>

      <Box
        ref={ref}
        role="listbox"
        aria-label="Forma de pagamento"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          flexDirection: isVertical ? 'column' : 'row',
          flexWrap: isVertical ? 'nowrap' : 'wrap',
          gap: isVertical ? 0.5 : 1,
          outline: 'none',
          borderRadius: '10px',
          // Só a listagem rola (mesmo maxHeight/overflow da lista vertical de
          // Faturamento, ver SeletorFormasPagamento.tsx) — a dialog em volta
          // não precisa mais crescer/rolar por causa dela.
          ...(isVertical ? { maxHeight: { xs: 260, md: 320 }, overflowY: 'auto', pr: 0.5 } : {}),
          '&:focus-visible': {
            outline: `2px solid ${theme.torq.sidebar.iconActive}`,
            outlineOffset: 2,
          },
        }}
      >
        {formas.map((forma, index) => {
          const isSelecionado = forma.id === selecionadoId;
          const isDestacado = forma.id === destacadoId && !isSelecionado;
          return (
            <Box
              key={forma.id}
              ref={(el: HTMLDivElement | null) => { itemRefs.current[index] = el; }}
              role="option"
              aria-selected={isSelecionado}
              onClick={() => { if (disabled) return; onDestacar(forma.id); onConfirmar(forma.id, false); }}
              sx={isVertical ? {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                pl: 1,
                pr: 3.5,
                borderRadius: '10px',
                cursor: disabled ? 'default' : 'pointer',
                border: `1.5px solid ${isSelecionado ? theme.torq.sidebar.iconActive : isDestacado ? alpha(theme.torq.sidebar.iconActive, 0.5) : 'transparent'}`,
                bgcolor: isSelecionado ? alpha(theme.torq.sidebar.iconActive, 0.1) : 'transparent',
                transition: 'border-color 0.12s, background-color 0.12s',
                '&:hover': disabled ? undefined : {
                  bgcolor: isSelecionado ? alpha(theme.torq.sidebar.iconActive, 0.14) : alpha(theme.palette.text.primary, 0.04),
                },
              } : {
                position: 'relative',
                flex: '1 1 130px',
                minWidth: 118,
                maxWidth: 160,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 1.25,
                px: 1,
                borderRadius: '12px',
                cursor: disabled ? 'default' : 'pointer',
                border: `1.5px solid ${isSelecionado ? theme.torq.sidebar.iconActive : isDestacado ? alpha(theme.torq.sidebar.iconActive, 0.5) : corBorda}`,
                bgcolor: isSelecionado ? alpha(theme.torq.sidebar.iconActive, 0.1) : 'background.paper',
                boxShadow: isSelecionado ? `0 2px 8px ${alpha(theme.torq.sidebar.iconActive, 0.18)}` : 'none',
                transition: 'border-color 0.12s, background-color 0.12s',
                '&:hover': disabled ? undefined : {
                  borderColor: theme.torq.sidebar.iconActive,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: isVertical ? '50%' : 6,
                  right: 6,
                  transform: isVertical ? 'translateY(-50%)' : undefined,
                  minWidth: 18,
                  height: 18,
                  px: 0.4,
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: isSelecionado ? theme.torq.sidebar.iconActive : alpha(theme.palette.text.primary, 0.08),
                  color: isSelecionado ? theme.torq.sidebar.bg : 'text.secondary',
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ color: isSelecionado ? theme.torq.sidebar.iconActive : 'text.secondary', display: 'flex', flexShrink: 0 }}>
                {forma.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: isVertical ? '0.82rem' : '0.78rem',
                  fontWeight: isSelecionado ? 700 : 500,
                  color: isSelecionado ? theme.torq.sidebar.iconActive : 'text.primary',
                  textAlign: isVertical ? 'left' : 'center',
                  lineHeight: 1.2,
                }}
              >
                {forma.nome}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

PaymentMethodSelector.displayName = 'PaymentMethodSelector';
